import React, { useState } from 'react';
import classNames from 'classnames';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { parse, stringify } from '../../../util/urlHelpers';
import { matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Avatar,
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  InlineTextButton,
  ModalMissingInformation,
  NamedLink,
} from '../../../components';

import MenuIcon from './MenuIcon';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import SidebarMenu from '../../../components/SidebarMenu/SidebarMenu';

import css from './Topbar.module.css';
import { isUserAuthorized } from '../../../util/userHelpers';

const MOBILE_MENU_BUTTON_ID = 'mobileMenuButton';

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return show ? (
    <div className={classes} role="alert">
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  ) : null;
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
  } = props;

  const [emailModalOpen, setEmailModalOpen] = useState(false);

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  // Use hooks for reliable router access (withRouter props can lag on first render)
  const routerHistory = useHistory();
  const routerLocation = useLocation();
  const loc = routerLocation || location;
  const hist = routerHistory || history;

  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(loc, routeConfiguration);

  const isApprovedUser = isAuthenticated && isUserAuthorized(currentUser);

  // Whether the current user needs to verify their email
  const emailUnverified = isApprovedUser && !!currentUser?.id && !currentUser?.attributes?.emailVerified;

  // Mobile: hamburger opens sidebar (sidemenu param) for approved users
  const openSideMenu = () => {
    const { pathname, state: locState } = loc;
    const searchString = `?${stringify({ sidemenu: 'open', ...parse(loc.search) })}`;
    hist.push(`${pathname}${searchString}`, locState);
  };

  const classes = classNames(rootClassName || css.root, className);

  const isLandingPage = resolvedCurrentPage === 'LandingPage';
  const isOnboardingPage = resolvedCurrentPage === 'OnboardingPage';

  // Mobile profile menu for authenticated users (all pages including LandingPage)
  const mobileProfileMenuMaybe = isApprovedUser ? (
    <Menu skipFocusOnNavigation={true}>
      <MenuLabel
        id="mobile-profile-menu-label"
        className={css.mobileProfileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
        ariaLabel="Profile menu"
      >
        <span className={css.avatarWrapper}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
          {emailUnverified && <span className={css.emailAlertBadge} title="Email not verified" />}
        </span>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {isLandingPage && (
          <MenuItem key="dashboard">
            <NamedLink name="DashboardPage" className={css.dashboardLink}>
              <span className={css.menuItemBorder} />
              My Account
            </NamedLink>
          </MenuItem>
        )}
        {emailUnverified && (
          <MenuItem key="verifyEmail">
            <InlineTextButton rootClassName={css.validateEmailButton} onClick={() => setEmailModalOpen(true)}>
              <span className={css.menuItemBorder} />
              <span className={css.validateEmailLabel}>
                <span className={css.validateEmailIcon}>!</span>
                Validate email
              </span>
            </InlineTextButton>
          </MenuItem>
        )}
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={handleLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  // Mobile auth links for visitors
  const mobileAuthLinksMaybe = !isAuthenticated ? (
    <div className={css.mobileAuthLinks}>
      <NamedLink name="SignupPage" className={css.mobileSignupLink}>
        Affiliate Login
      </NamedLink>
    </div>
  ) : null;

  return (
    <div className={classes}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      {/* SidebarMenu mounted here (mobileOnly) ensures the hamburger overlay works on every page */}
      <SidebarMenu currentPage={resolvedCurrentPage} mobileOnly />
      <nav className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        {isApprovedUser && !isOnboardingPage ? (
          <Button id={MOBILE_MENU_BUTTON_ID} rootClassName={css.menu} onClick={openSideMenu} title={intl.formatMessage({ id: 'Topbar.menuIcon' })}>
            <MenuIcon className={css.menuIcon} ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })} />
          </Button>
        ) : (!isLandingPage || isAuthenticated) ? (
          <div className={css.menu} />
        ) : null}
        <LinkedLogo
          id="logo-topbar-mobile"
          layout={'mobile'}
          className={css.logoLinkMobile}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        {mobileProfileMenuMaybe}
        {mobileAuthLinksMaybe}
      </nav>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          intl={intl}
          isAuthenticated={isAuthenticated}
          onLogout={handleLogout}
          onOpenEmailModal={() => setEmailModalOpen(true)}
          emailUnverified={emailUnverified}
          config={config}
        />
      </div>
      <ModalMissingInformation
        containerClassName={css.missingInformationModal}
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        currentUser={currentUser}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
